<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  row: { type: Object, default: () => ({}) },
  conversation: { type: Object, default: null },
  sidebarWaiting: { type: String, default: '—' },
});

const asObject = value => (value && typeof value === 'object' ? value : {});
const asArray = value => (Array.isArray(value) ? value : []);
const normalizeText = value => String(value || '').trim();
const displayValue = value => normalizeText(value) || '—';
const isHttpUrl = value => /^https?:\/\//i.test(String(value || '').trim());

const additionalAttributes = computed(() =>
  asObject(props.contact?.additional_attributes)
);
const convoAdditional = computed(() =>
  asObject(props.conversation?.additional_attributes)
);
const convoCustom = computed(() =>
  asObject(props.conversation?.custom_attributes)
);

const heroName = computed(() => {
  return (
    normalizeText(props.contact?.name) ||
    normalizeText(props.row?.name) ||
    normalizeText(props.contact?.email) ||
    'Visitor'
  );
});

const heroEmail = computed(() => normalizeText(props.contact?.email) || '');

const locationLabel = computed(() => {
  const fromRow = [props.row?.city, props.row?.state, props.row?.country]
    .map(normalizeText)
    .filter(Boolean)
    .join(', ');
  if (fromRow) return fromRow;

  const add = additionalAttributes.value;
  const fromAttrs = [
    add.city,
    add.state || add.region,
    add.country || add.country_name,
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(', ');
  return fromAttrs;
});

const mapEmbedUrl = computed(() => {
  const q = normalizeText(locationLabel.value);
  if (!q) return '';
  return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
});

const fmtDurationPlain = totalSeconds => {
  const s = Math.max(0, Number(totalSeconds || 0));
  if (!Number.isFinite(s)) return '—';
  if (s < 60) return `${Math.floor(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.floor(s % 60)}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const visitsCount = computed(() => {
  const add = additionalAttributes.value;
  const fallback =
    add.traffic_visits_count ||
    add.visits_count ||
    add.visits ||
    props.row?.visits_count;
  const n = Number(fallback);
  return Number.isFinite(n) && n > 0 ? n : 1;
});

const chatsCount = computed(() => {
  const n = Number(props.row?.chats_count);
  return Number.isFinite(n) && n >= 0 ? n : 0;
});

const cameFromUrl = computed(() => {
  return (
    normalizeText(props.row?.came_from) ||
    normalizeText(additionalAttributes.value?.referer) ||
    normalizeText(additionalAttributes.value?.referrer) ||
    normalizeText(convoAdditional.value?.referer) ||
    ''
  );
});

const chatDuration = computed(() => {
  const start = Number(props.conversation?.created_at);
  const end =
    Number(props.conversation?.last_activity_at) ||
    Number(props.conversation?.updated_at);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
    return '';
  return fmtDurationPlain(end - start);
});

const groupsLabel = computed(() => {
  const groups = asArray(props.row?.groups).map(normalizeText).filter(Boolean);
  return groups.length ? groups.join(', ') : '';
});

const visitedPages = computed(() => {
  const pages = asArray(additionalAttributes.value?.traffic_pages)
    .filter(p => p && typeof p === 'object')
    .map(p => ({
      url: normalizeText(p.url),
      durationSeconds: Number(p.duration_seconds),
    }))
    .filter(p => p.url);

  const currentUrl =
    normalizeText(additionalAttributes.value?.last_page) ||
    normalizeText(props.row?.last_page);
  if (currentUrl) {
    const seen = new Set(pages.map(p => p.url));
    if (!seen.has(currentUrl)) {
      const sessionSeconds = Number(props.row?.time_on_all_pages);
      pages.unshift({
        url: currentUrl,
        durationSeconds: Number.isFinite(sessionSeconds) ? sessionSeconds : NaN,
      });
    }
  }

  return pages.slice(0, 10);
});

const formatFormValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(v => String(v || '').trim())
      .filter(Boolean)
      .join(', ');
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }
  return String(value || '').trim();
};

const normalizeFormEntries = formData => {
  if (!formData || typeof formData !== 'object') return [];
  const responses = formData.responses;
  if (!responses) return [];

  if (Array.isArray(responses)) {
    return responses
      .map((row, index) => {
        const label = row?.label || row?.type || `Field ${index + 1}`;
        const value = formatFormValue(row?.value);
        if (!value) return null;
        return { key: row?.id || `row_${index}`, label, value };
      })
      .filter(Boolean);
  }

  if (typeof responses === 'object') {
    return Object.entries(responses)
      .map(([key, row], index) => {
        if (row && typeof row === 'object' && 'value' in row) {
          const label = row.label || key || `Field ${index + 1}`;
          const value = formatFormValue(row.value);
          if (!value) return null;
          return { key, label, value };
        }
        const value = formatFormValue(row);
        if (!value) return null;
        return { key, label: key || `Field ${index + 1}`, value };
      })
      .filter(Boolean);
  }

  return [];
};

const extractLegacyPreChatEntries = () => {
  const messages = asArray(props.conversation?.messages);
  if (!messages.length) return [];
  const sorted = [...messages].sort(
    (a, b) => Number(a?.created_at || 0) - Number(b?.created_at || 0)
  );
  const firstIncoming = sorted.find(m => {
    return (
      Number(m?.message_type) === 0 &&
      !m?.private &&
      String(m?.content || '').trim().length
    );
  });
  if (!firstIncoming) return [];

  return String(firstIncoming.content || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const colonSeparated = line.match(/^([^:]{1,120}):\s*(.+)$/);
      if (colonSeparated) {
        return {
          key: `legacy_pre_chat_${index}`,
          label: colonSeparated[1].trim(),
          value: colonSeparated[2].trim(),
        };
      }
      return {
        key: `legacy_pre_chat_${index}`,
        label: `Field ${index + 1}`,
        value: line,
      };
    });
};

const preChatEntries = computed(() => {
  const structured = normalizeFormEntries(convoCustom.value?.wlab_prechat_form);
  if (structured.length) return structured;
  return extractLegacyPreChatEntries();
});

const technologyRows = computed(() => {
  const browser =
    asObject(convoAdditional.value?.browser) ||
    asObject(additionalAttributes.value?.traffic_browser);
  const platformBase = [browser.platform_name, browser.platform_version]
    .map(normalizeText)
    .filter(Boolean)
    .join(' ');
  const deviceName = normalizeText(browser.device_name);
  const browserName = [browser.browser_name, browser.browser_version]
    .map(normalizeText)
    .filter(Boolean)
    .join(' ');
  const osDevice = [
    platformBase,
    deviceName && deviceName !== 'Unknown' ? deviceName : '',
  ]
    .filter(Boolean)
    .join(' / ');

  const ip =
    normalizeText(additionalAttributes.value?.traffic_ip) ||
    normalizeText(convoAdditional.value?.created_at_ip) ||
    '';
  const userAgent =
    normalizeText(additionalAttributes.value?.traffic_user_agent) ||
    normalizeText(convoAdditional.value?.user_agent) ||
    '';

  return [
    { key: 'ip', label: 'IP address', value: displayValue(ip) },
    { key: 'os', label: 'OS/Device', value: displayValue(osDevice) },
    { key: 'browser', label: 'Browser', value: displayValue(browserName) },
    { key: 'ua', label: 'User agent', value: displayValue(userAgent) },
  ];
});
</script>

<template>
  <div class="etcs">
    <div class="etcs__hero">
      <Avatar
        :src="contact.thumbnail"
        :name="heroName"
        :status="contact.availability_status"
        :size="64"
        hide-offline-status
        rounded-full
      />
      <h3 class="etcs__name">{{ heroName }}</h3>
      <p v-if="heroEmail" class="etcs__sub">{{ heroEmail }}</p>
      <p v-if="locationLabel" class="etcs__sub">{{ locationLabel }}</p>
    </div>

    <div v-if="mapEmbedUrl" class="etcs__map">
      <iframe
        class="etcs__map-iframe"
        :src="mapEmbedUrl"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        title="Location map"
      />
    </div>

    <section class="etcs__card">
      <div class="etcs__card-title">Activity</div>
      <div class="etcs__grid etcs__grid--pad">
        <div class="etcs__row">
          <span class="etcs__k">Activity</span>
          <span class="etcs__v">{{ row.activity || '—' }}</span>
        </div>
        <div class="etcs__row">
          <span class="etcs__k">Customer waiting</span>
          <span class="etcs__v">{{ sidebarWaiting }}</span>
        </div>
      </div>
    </section>

    <details class="etcs__acc" open>
      <summary class="etcs__sum">Additional info</summary>
      <div class="etcs__grid etcs__grid--pad">
        <div class="etcs__row">
          <span class="etcs__k">Returning visitor</span>
          <span class="etcs__v">{{ visitsCount }} visit<span v-if="visitsCount !== 1">s</span>,
            {{ chatsCount }} chat<span v-if="chatsCount !== 1">s</span></span>
        </div>
        <div class="etcs__row">
          <span class="etcs__k">Came from</span>
          <span class="etcs__v">
            <a
              v-if="cameFromUrl && isHttpUrl(cameFromUrl)"
              :href="cameFromUrl"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="etcs__link"
            >
              {{ cameFromUrl }}
            </a>
            <template v-else>{{ cameFromUrl || '—' }}</template>
          </span>
        </div>
        <div class="etcs__row">
          <span class="etcs__k">Chat duration</span>
          <span class="etcs__v">{{ chatDuration || '—' }}</span>
        </div>
        <div class="etcs__row">
          <span class="etcs__k">Groups</span>
          <span class="etcs__v">{{ groupsLabel || '—' }}</span>
        </div>
      </div>
    </details>

    <details class="etcs__acc">
      <summary class="etcs__sum">Visited pages</summary>
      <div class="etcs__list">
        <div v-if="!visitedPages.length" class="etcs__empty">—</div>
        <div
          v-for="(p, idx) in visitedPages"
          :key="`${p.url}-${idx}`"
          class="etcs__page"
        >
          <div class="etcs__page-url">
            <a
              v-if="isHttpUrl(p.url)"
              :href="p.url"
              target="_blank"
              rel="noopener noreferrer nofollow"
              class="etcs__link"
            >
              {{ p.url }}
            </a>
            <template v-else>{{ p.url }}</template>
          </div>
          <div class="etcs__page-dur">
            {{
              Number.isFinite(p.durationSeconds)
                ? fmtDurationPlain(p.durationSeconds)
                : '—'
            }}
          </div>
        </div>
      </div>
    </details>

    <details class="etcs__acc">
      <summary class="etcs__sum">Pre-chat form</summary>
      <div class="etcs__grid etcs__grid--pad">
        <div v-if="!preChatEntries.length" class="etcs__empty">—</div>
        <div v-for="entry in preChatEntries" :key="entry.key" class="etcs__row">
          <span class="etcs__k">{{ entry.label }}</span>
          <span class="etcs__v">{{ entry.value }}</span>
        </div>
      </div>
    </details>

    <details class="etcs__acc">
      <summary class="etcs__sum">Technology</summary>
      <div class="etcs__grid etcs__grid--pad">
        <div
          v-for="rowItem in technologyRows"
          :key="rowItem.key"
          class="etcs__row"
        >
          <span class="etcs__k">{{ rowItem.label }}</span>
          <span class="etcs__v">{{ rowItem.value }}</span>
        </div>
      </div>
    </details>
  </div>
</template>

<style scoped>
.etcs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
}

.etcs__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 10px 10px 4px;
}

.etcs__name {
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  color: #111827;
  line-height: 1.2;
  word-break: break-word;
}

.etcs__sub {
  margin: 0;
  font-size: 12px;
  color: rgba(17, 24, 39, 0.66);
  line-height: 1.2;
  word-break: break-word;
}

.etcs__map {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(17, 24, 39, 0.1);
  background: #fff;
}

.etcs__map-iframe {
  width: 100%;
  height: 180px;
  border: 0;
}

.etcs__card {
  border: 1px solid rgba(17, 24, 39, 0.1);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.etcs__card-title {
  padding: 10px 12px;
  background: rgba(17, 24, 39, 0.03);
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  font-size: 13px;
  font-weight: 650;
  color: #111827;
}

.etcs__grid {
  display: grid;
  grid-template-columns: 1fr;
}

.etcs__grid--pad {
  padding: 10px 12px;
  gap: 10px;
}

.etcs__row {
  display: grid;
  grid-template-columns: minmax(120px, 160px) minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.etcs__k {
  font-size: 12px;
  color: rgba(17, 24, 39, 0.6);
}

.etcs__v {
  font-size: 12px;
  color: #111827;
  word-break: break-word;
}

.etcs__acc {
  border: 1px solid rgba(17, 24, 39, 0.1);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.etcs__sum {
  cursor: pointer;
  list-style: none;
  padding: 10px 12px;
  background: rgba(17, 24, 39, 0.03);
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  font-size: 13px;
  font-weight: 650;
  color: #111827;
}

.etcs__sum::-webkit-details-marker {
  display: none;
}

.etcs__sum::after {
  content: '▾';
  float: right;
  color: rgba(17, 24, 39, 0.55);
  transform: rotate(-90deg);
  transition: transform 120ms ease;
}

details[open] > .etcs__sum::after {
  transform: rotate(0deg);
}

.etcs__list {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.etcs__page {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
}

.etcs__page-url {
  font-size: 12px;
  color: #111827;
  word-break: break-word;
}

.etcs__page-dur {
  font-size: 12px;
  color: rgba(17, 24, 39, 0.66);
  white-space: nowrap;
}

.etcs__link {
  color: #2563eb;
  text-decoration: none;
}

.etcs__link:hover {
  text-decoration: underline;
}

.etcs__empty {
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(17, 24, 39, 0.5);
}
</style>
